<template>
<div class="wizard-wrapper">
  <div class="donut-container" v-if="macroTotal() > 0 && !start">
    <svg class="donut-chart">
      <g v-for="(value, index) in customMacroValues">
        <circle
          :class="labels[index]"
          :cx="cx"
          :cy="cy"
          :r="radius"
          :stroke-width="strokeWidth"
          :stroke-dasharray="adjustedCircumference"
          :stroke-dashoffset="calculateStrokeDashOffset(value, circumference)"
          fill="transparent"
          :transform="returnCircleTransformValue(index)"
        />

        <text class="label" v-if="segmentBigEnough(value)" text-anchor="middle" dx="-10px" dy="10px" :x="chartData[index].textX" :y="chartData[index].textY">
          {{ percentageString(value) }}
        </text>
      </g>

      <g v-for="(value, index) in macroValues">
        <line class="section-divider" :x1="cx" :y1="cy" :stroke-width="10" :transform="returnLineTransformValue(index)" />
      </g>
    </svg>

    <BaseButton type="button" class="recalculate-macros btn-secondary btn-with-icon btn-ghost btn-block" @click="start = true">
      <span>Recalculate Macros</span>
    </BaseButton>

    <div class="chart-key">
      <div :class="`${labels[0]}-section`">
        <h2>{{ macroValues[0] }}g</h2>
        <h4>{{ labels[0] }}</h4>
      </div>
      <div :class="`${labels[1]}-section`">
        <h2>{{ macroValues[1] }}g</h2>
        <h4>{{ labels[1] }}</h4>
      </div>
      <div :class="`${labels[2]}-section`">
        <h2>{{ macroValues[2] }}g</h2>
        <h4>{{ labels[2] }}</h4>
      </div>
    </div>
    <div class="chart-key mt-3 information">
      <div class="card w-100 mx-3 text-left">
        <div class="card-header h4">Information</div>

        <div class="card-body">
          <p>
            {{ $t('lblMacrosWarning') }}
          </p>
        </div>
        <div class="alert alert-warning mt-2 h4 mb-0"><span class="text-upper">Important</span>: Consult your doctor</div>
      </div>
    </div>
  </div>

  <div v-if="macroTotal() <= 0 || start">
    <horizontal-stepper :reset="reset" :steps="steps" @completed-step="completeStep" @active-step="isStepActive" @stepper-finished="stepperFinished" />
  </div>
</div>
</template>

<script>
import appConfig from '@src/app.config.ES6';
import store from '@state/store';
import { macroCalculatorComputed, macroCalculatorMethods, userComputed, userMethods } from '@state/helpers';
import HorizontalStepper from 'vue-stepper';
import MacroStepOne from './macro-step-one';
import MacroStepTwo from './macro-step-two';
import MacroStepThree from './macro-step-three';
import MacroStepFour from './macro-step-four';
import MacroStepFive from './macro-step-five';

export default {
  components: {
    HorizontalStepper,
    MacroStepOne,
    MacroStepTwo,
    MacroStepThree,
    MacroStepFour,
  },
  props: {
    page() {
      return {
        title: '',
        meta: [
          {
            name: 'description',
            content: `.`,
          },
        ],
      };
    },
    profile: {
      type: Object,
    },
    error: {
      type: Object,
    },
    userAccessToken: {
      type: String,
    },
  },
  data() {
    return {
      appConfig,
      angleOffset: -90,
      chartData: [],
      labels: ['Carbs', 'Protein', 'Fat'],
      cx: 157,
      cy: 157,
      radius: 75,
      macroValues: [],
      customMacroValues: [],
      strokeWidth: 150,

      start: false,
      reset: false,
      steps: [
        {
          name: 'MacroStepOne',
          component: MacroStepOne,
          completed: false,
        },
        {
          name: 'MacroStepTwo',
          component: MacroStepTwo,
          completed: false,
        },
        {
          name: 'MacroStepThree',
          component: MacroStepThree,
          completed: false,
        },
        {
          name: 'MacroStepFour',
          component: MacroStepFour,
          completed: false,
        },
      ],
    };
  },
  computed: {
    ...macroCalculatorComputed,
    ...userComputed,

    // adjust the circumference to add small white gaps
    adjustedCircumference() {
      // return this.circumference - 10;
      return this.circumference;
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    dataTotal() {
      return this.customMacroValues.reduce((acc, val) => acc + val);
    },
    calculateChartData() {
      this.angleOffset = -90;
      this.chartData = [];
      this.macroValues = [this.userProfile.carb, this.userProfile.protein, this.userProfile.fat];

      if (this.appConfig.NATY_MACROS) {
        this.customMacroValues = [this.userProfile.carb, this.userProfile.protein, this.userProfile.fat * 2];
      } else {
        this.customMacroValues = [this.userProfile.carb, this.userProfile.protein, this.userProfile.fat];
      }
      this.customMacroValues.forEach((dataVal, index) => {
        const { textX, textY } = this.calculateTextCoords(dataVal, this.angleOffset);
        // start at -90deg so that the largest segment is perpendicular to top
        const data = {
          degrees: this.angleOffset,
          textX,
          textY,
        };
        this.chartData.push(data);
        this.angleOffset = this.dataPercentage(dataVal) * 360 + this.angleOffset;
      });
    },
  },
  mounted() {
    if (this.userProfile) {
      this.calculateChartData;
    }

    // Add another step if client has custom macros enabled
    if (this.steps.length && this.appConfig.CUSTOM_MACROS) {
      this.steps.push({
        name: 'MacroStepFive',
        component: MacroStepFive,
        completed: false,
      });
    }
  },
  methods: {
    ...macroCalculatorMethods,
    ...userMethods,

    // Executed when @completed-step event is triggered
    completeStep(payload) {
      this.steps.forEach((step) => {
        if (step.name === payload.name) {
          step.completed = true;
        }
      });
    },
    // Executed when @active-step event is triggered
    isStepActive(payload) {
      this.steps.forEach((step) => {
        if (step.name === payload.name) {
          if (step.completed === true) {
            step.completed = false;
          }
        }
      });
    },
    // Executed when @stepper-finished event is triggered
    async stepperFinished(payload) {
      // Rounding values so as not to break the backend (invalid input syntax for integer on decimals)
      await this.updateUserProfile({ fat: Math.round(this.fat), carb: Math.round(this.carbs), protein: Math.round(this.protein) });
      const vm = this;
      this.start = false;
      this.reset = true;
      this.$nextTick().then(() => {
        vm.reset = false;
      });
    },
    macroTotal() {
      return this.userProfile.carb + this.userProfile.protein + this.userProfile.fat;
    },
    calculateStrokeDashOffset(dataVal, circumference) {
      const strokeDiff = this.dataPercentage(dataVal) * circumference;
      return circumference - strokeDiff;
    },
    calculateTextCoords(dataVal, angleOffset) {
      // t must be radians
      // x(t) = r cos(t) + j
      // y(t) = r sin(t) + j

      const angle = (this.dataPercentage(dataVal) * 360) / 2 + angleOffset;
      const radians = this.degreesToRadians(angle);

      const textCoords = {
        textX: (this.radius + 15) * Math.cos(radians) + this.cx,
        textY: (this.radius + 15) * Math.sin(radians) + this.cy - 5,
      };
      return textCoords;
    },
    degreesToRadians(angle) {
      return angle * (Math.PI / 180);
    },
    dataPercentage(dataVal) {
      return dataVal / this.dataTotal;
    },
    percentageString(dataVal) {
      return `${Math.round(this.dataPercentage(dataVal) * 100)}%`;
    },
    returnCircleTransformValue(index) {
      return `rotate(${this.chartData[index].degrees}, ${this.cx}, ${this.cy})`;
    },
    returnLineTransformValue(index) {
      return `rotate(${this.chartData[index].degrees + 135}, ${this.cx}, ${this.cy})`;
    },
    segmentBigEnough(dataVal) {
      return Math.round(this.dataPercentage(dataVal) * 100) > 5;
    },
  },
  watch: {
    userProfile() {
      this.calculateChartData;
    },
  },
};
</script>

<style lang="scss">
@import 'theme.scss';

.btn-primary {
  background-color: $color-action;
  border: none;
  border-radius: 40px;
  color: $color-white;
  display: block;
  margin: 0 auto;
  margin-bottom: 2rem;
  padding: 10px 30px;
}

.section-divider {
  stroke: $color-body-bg;
}

.wizard-wrapper {
  @extend %scrollable-drawer-content;

  background: $color-body-bg;

  h1 {
    @extend %typography-medium;

    font-size: 2.8rem;
    font-weight: bold;
    line-height: 3rem;
    margin-bottom: 2rem;
  }

  .stepper-box {
    background-color: $color-body-bg;
    box-shadow: none;
    padding: 2rem 2rem 12rem;

    // iPhone 6 up
    @include media('md', null) {
      padding-bottom: 20rem;
    }

    .top {
      display: none;
    }

    > .bottom {
      border-top: none;
      padding: 0;
    }

    .stepper-button {
      display: flex;
      font-size: 1.7rem;
      padding: 0.5rem 1rem;

      &.next {
        background-color: $color-action !important;
        border-radius: 50rem;
        box-shadow: none;
        color: $color-white;
        display: block;
        min-width: 10rem;
        padding: 1rem 4rem;
        text-align: center;

        .material-icons {
          display: none;
          visibility: hidden;
        }
      }
    }
  }

  .activity-selector {
    border: 1px solid #e9e9e9;
    border-radius: 1rem !important;
    font-size: 1.7rem;
    letter-spacing: 0.053rem;
    line-height: 2.4rem;
    padding: 1.6rem 0;
    width: 100%;

    .image {
      color: green;
      height: 2.4rem;
      margin: 0 1.2rem;
      width: 2.4rem;
    }
  }

  .cdc {
    background-color: #1c798c !important;
  }
}

.donut-container {
  align-items: center;
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: absolute;
  right: 0;
  text-align: center;
  top: -5rem;
  width: 100%;

  // iPhone 6 up
  @include media('md', null) {
    top: 0;
  }

  .recalculate-macros {
    margin: 0 auto;
    position: relative;
    top: -5rem;
    width: auto !important;

    // iPhone 6 up
    @include media('md', null) {
      position: initial;
      top: 0;
    }
  }

  svg {
    display: inline-block;
    height: 315px;
    transform: scale(0.6);
    width: 315px;

    // iPhone 6 up
    @include media('md', null) {
      transform: scale(0.9);
    }

    g {
      circle {
        &.Carbs {
          stroke: $color-carbs;
        }
        &.Protein {
          stroke: $color-protein;
        }
        &.Fat {
          stroke: $color-fat;
        }
      }
    }

    text.label {
      fill: $color-white;
      font-size: 3rem;
      font-weight: bold;
    }
    text.values {
      fill: $color-white;
      font-size: 1.3rem;
      font-weight: bold;
    }
  }

  .information {
    height: 20rem !important;

    .card-body {
      overflow-y: scroll;
    }
  }

  .chart-key {
    display: flex;
    height: 10rem;
    justify-content: space-around;
    left: 0;
    position: relative;
    right: 0;
    text-align: center;
    top: 0;
    width: 100%;

    // iPhone 6 up
    @include media('md', null) {
      top: 3rem;
    }

    [class$='-section'] {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      padding-top: 2rem;
      width: 100%;

      h2 {
        margin: 0 0 1rem;
      }
    }

    .Carbs-section,
    .Protein-section,
    .Fat-section {
      h3 {
        font-weight: normal;
      }
    }

    .Carbs-section {
      background-color: $color-carbs-key;
      border-top: solid $color-carbs 1rem;
    }

    .Protein-section {
      background-color: $color-protein-key;
      border-top: solid $color-protein 1rem;
    }

    .Fat-section {
      background-color: $color-fat-key;
      border-top: solid $color-fat 1rem;
    }
  }
}
</style>
